import React, { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { apiClient, formatApiError } from "../lib/api";
import {
  Bell, CheckCheck, Archive, Settings, Loader2, ShieldCheck,
  MessageSquareWarning, ShoppingBag, CreditCard, Megaphone, UserPlus, FileText,
} from "lucide-react";
import NotificationDetailModal from "../components/NotificationDetailModal";

/**
 * iter223 — notification content is always displayed in the user's
 * active UI language. Backend `notify()` writes `title_i18n` /
 * `body_i18n` = {ar, en} on every notification (legacy rows keep
 * `title` / `body` only). This helper prefers the bundle for the
 * active `lang` and falls back to the plain string on legacy rows,
 * so no user ever sees mixed-language content.
 */
export function pickI18n(bundleOrString, plain, lang) {
  if (bundleOrString && typeof bundleOrString === "object") {
    return bundleOrString[lang] || bundleOrString.ar || bundleOrString.en || plain || "";
  }
  return plain || "";
}

const CATEGORY_META = {
  account: { ar: "الحساب", en: "Account", icon: UserPlus },
  kyc: { ar: "التوثيق", en: "Verification", icon: ShieldCheck },
  listings: { ar: "الإعلانات", en: "Listings", icon: FileText },
  messages: { ar: "الرسائل", en: "Messages", icon: ShoppingBag },
  disputes: { ar: "النزاعات", en: "Disputes", icon: MessageSquareWarning },
  payments: { ar: "المدفوعات", en: "Payments", icon: CreditCard },
  admin: { ar: "الإدارة", en: "Admin", icon: Megaphone },
};

/* ─── Notifications list page ────────────────────────────────────────── */
export default function NotificationsPage() {
  const { user } = useAuth();
  const { lang } = useI18n();
  const [filter, setFilter] = useState("all");
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [busy, setBusy] = useState(true);
  const [err, setErr] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setBusy(true); setErr(null);
    try {
      const params = { limit: 100, include_archived: showArchived };
      if (filter !== "all") params.category = filter;
      if (unreadOnly) params.unread_only = true;
      const { data } = await apiClient.get("/notifications", { params });
      setItems(data.items || []);
      setUnread(data.unread || 0);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };
  useEffect(() => { if (user) load(); }, [user, filter, unreadOnly, showArchived]);

  if (user === undefined) return <div className="p-10 text-[#525252]">Loading…</div>;
  if (!user) return <Navigate to="/login" state={{ from: "/notifications" }} replace />;

  const click = async (n) => {
    if (!n.read) {
      await apiClient.post(`/notifications/${n.id}/read`);
      setItems((p) => p.map((x) => x.id === n.id ? { ...x, read: true } : x));
      setUnread((c) => Math.max(0, c - 1));
    }
    setSelected(n);
  };

  const archive = async (n, e) => {
    e.stopPropagation();
    await apiClient.post(`/notifications/${n.id}/archive`);
    setItems((p) => p.filter((x) => x.id !== n.id));
  };

  const markAll = async () => {
    await apiClient.post("/notifications/read-all");
    setItems((p) => p.map((n) => ({ ...n, read: true })));
    setUnread(0);
  };

  const archiveRead = async () => {
    await apiClient.post("/notifications/archive-read");
    load();
  };

  return (
    <div className="max-w-3xl mx-auto px-5 sm:px-8 py-10" data-testid="notifications-page">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div>
          <div className="overline overline-gold mb-2 inline-flex items-center gap-2">
            <Bell className="w-3 h-3" /> {lang === "ar" ? "الإشعارات" : "Notifications"}
          </div>
          <h1 className="font-display text-3xl sm:text-4xl tracking-[-0.02em] font-medium">
            {lang === "ar" ? "كل الإشعارات" : "All notifications"}
            {unread > 0 && <span className="ms-2 text-sm font-normal text-[#8a6f2e]">{unread} {lang === "ar" ? "غير مقروءة" : "unread"}</span>}
          </h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={markAll} disabled={unread === 0} className="btn-ghost text-sm disabled:opacity-40" data-testid="notif-page-mark-all">
            <CheckCheck className="w-4 h-4" /> {lang === "ar" ? "اقرأ الكل" : "Mark all read"}
          </button>
          <button onClick={archiveRead} className="btn-ghost text-sm" data-testid="notif-page-archive-read">
            <Archive className="w-4 h-4" /> {lang === "ar" ? "أرشف المقروءة" : "Archive read"}
          </button>
          <Link to="/settings/notifications" className="btn-ghost text-sm" data-testid="notif-page-settings">
            <Settings className="w-4 h-4" /> {lang === "ar" ? "الإعدادات" : "Settings"}
          </Link>
        </div>
      </div>

      {/* Category filter pills */}
      <div className="flex flex-wrap gap-2 mt-4 mb-3" data-testid="notif-filters">
        {[["all", lang === "ar" ? "الكل" : "All"], ...Object.entries(CATEGORY_META).map(([k, v]) => [k, v[lang === "ar" ? "ar" : "en"]])].map(([k, label]) => (
          <button key={k} onClick={() => setFilter(k)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all border ${filter === k ? "bg-[#0a0a0a] border-[#0a0a0a] text-[#c9a55c]" : "bg-white border-[#0a0a0a14] text-[#3a3a3a] hover:border-[#c9a55c]"}`}
            data-testid={`notif-cat-${k}`}
          >
            {label}
          </button>
        ))}
        <label className="ms-auto inline-flex items-center gap-1.5 text-xs text-[#525252] cursor-pointer">
          <input type="checkbox" checked={unreadOnly} onChange={(e) => setUnreadOnly(e.target.checked)} data-testid="notif-unread-only" />
          {lang === "ar" ? "غير المقروءة فقط" : "Unread only"}
        </label>
        <label className="inline-flex items-center gap-1.5 text-xs text-[#525252] cursor-pointer">
          <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} data-testid="notif-show-archived" />
          {lang === "ar" ? "إظهار المؤرشفة" : "Show archived"}
        </label>
      </div>

      {err && <div className="rounded-xl p-3 text-sm bg-red-50 border border-red-200 text-red-700">{String(err)}</div>}

      {busy ? (
        <div className="text-center py-12"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
      ) : items.length === 0 ? (
        <div className="surface rounded-2xl p-16 text-center text-[#6b6b6b]" data-testid="notif-page-empty">
          <Bell className="w-10 h-10 mx-auto text-[#c9a55c] mb-3" />
          {lang === "ar" ? "لا توجد إشعارات لعرضها." : "Nothing to show here."}
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((n) => {
            const meta = CATEGORY_META[n.category] || CATEGORY_META.admin;
            const Icon = meta.icon;
            return (
              <div key={n.id} onClick={() => click(n)} className={`surface rounded-2xl p-4 cursor-pointer hover:shadow-md transition-all flex items-start gap-3 ${n.read ? "" : "ring-2 ring-[#c9a55c]/30"}`} data-testid={`notif-row-${n.id}`}>
                <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: "rgba(201,165,92,0.12)", color: "#8a6f2e" }}>
                  <Icon className="w-5 h-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold text-[#0a0a0a]">{pickI18n(n.title_i18n, n.title, lang)}</h3>
                    {!n.read && <span className="text-[10px] font-bold bg-[#c9a55c]/20 text-[#8a6f2e] rounded-full px-2 py-0.5">{lang === "ar" ? "جديد" : "NEW"}</span>}
                    {n.archived && <span className="text-[10px] font-bold bg-[#0a0a0a]/[0.06] text-[#525252] rounded-full px-2 py-0.5">{lang === "ar" ? "مؤرشف" : "ARCHIVED"}</span>}
                  </div>
                  <p className="text-sm text-[#525252] mt-1 whitespace-pre-wrap">{pickI18n(n.body_i18n, n.body, lang)}</p>
                  <div className="text-[10px] text-[#a8a8a8] mt-1.5 inline-flex items-center gap-2">
                    <span>{meta[lang === "ar" ? "ar" : "en"]}</span>
                    <span>·</span>
                    <span>{new Date(n.created_at).toLocaleString(lang === "ar" ? "ar-SA" : "en-GB")}</span>
                  </div>
                </div>
                {!n.archived && (
                  <button onClick={(e) => archive(n, e)} className="text-[#a8a8a8] hover:text-[#0a0a0a] p-1" aria-label="Archive" data-testid={`notif-archive-${n.id}`}>
                    <Archive className="w-4 h-4" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
      {selected && (
        <NotificationDetailModal notification={selected} onClose={() => setSelected(null)} lang={lang} />
      )}
    </div>
  );
}

/* ─── Settings page (preferences) ────────────────────────────────────── */
export function NotificationSettingsPage() {
  const { user } = useAuth();
  const { lang } = useI18n();
  const [prefs, setPrefs] = useState(null);
  const [emailEnabled, setEmailEnabled] = useState(false);
  const [busy, setBusy] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!user) return;
    apiClient.get("/notifications/preferences").then(({ data }) => {
      setPrefs(data.preferences); setEmailEnabled(data.email_enabled);
    }).finally(() => setBusy(false));
  }, [user]);

  if (user === undefined) return <div className="p-10 text-[#525252]">Loading…</div>;
  if (!user) return <Navigate to="/login" state={{ from: "/settings/notifications" }} replace />;

  const toggle = (cat) => setPrefs((p) => ({ ...p, [cat]: !p[cat] }));

  const save = async () => {
    setSaving(true); setSaved(false);
    try {
      await apiClient.patch("/notifications/preferences", { ...prefs, email_enabled: emailEnabled });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } finally { setSaving(false); }
  };

  if (busy || !prefs) return <div className="p-10 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div>;

  return (
    <div className="max-w-2xl mx-auto px-5 sm:px-8 py-10" data-testid="notif-settings-page">
      <Link to="/notifications" className="text-xs text-[#8a6f2e] hover:underline">← {lang === "ar" ? "الإشعارات" : "Notifications"}</Link>
      <h1 className="font-display text-3xl tracking-[-0.02em] font-medium mt-2 mb-2">{lang === "ar" ? "إعدادات الإشعارات" : "Notification settings"}</h1>
      <p className="text-sm text-[#6b6b6b] mb-6">{lang === "ar" ? "اختر أنواع الأحداث التي تريد تلقي إشعارات عنها." : "Choose which categories of events you want to receive."}</p>

      <div className="surface rounded-2xl p-6 space-y-1" data-testid="notif-pref-list">
        {Object.entries(CATEGORY_META).map(([k, m]) => {
          const Icon = m.icon;
          return (
            <label key={k} className="flex items-center justify-between gap-3 py-3 border-b border-[#0a0a0a]/[0.06] last:border-0 cursor-pointer">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ backgroundColor: "rgba(201,165,92,0.12)", color: "#8a6f2e" }}>
                  <Icon className="w-4 h-4" />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-[#0a0a0a]">{m[lang === "ar" ? "ar" : "en"]}</div>
                </div>
              </div>
              <Switch checked={prefs[k]} onChange={() => toggle(k)} testid={`pref-${k}`} />
            </label>
          );
        })}
      </div>

      <div className="surface rounded-2xl p-6 mt-4">
        <label className="flex items-center justify-between gap-3 cursor-pointer">
          <div>
            <div className="text-sm font-semibold text-[#0a0a0a]">{lang === "ar" ? "إشعارات البريد الإلكتروني" : "Email notifications"}</div>
            <div className="text-xs text-[#6b6b6b] mt-0.5">{lang === "ar" ? "إرسال نسخة بالبريد للأحداث المهمة." : "Send a copy of important events to your inbox."}</div>
          </div>
          <Switch checked={emailEnabled} onChange={() => setEmailEnabled((v) => !v)} testid="pref-email" />
        </label>
      </div>

      <div className="flex justify-end gap-2 mt-5">
        {saved && <span className="text-sm text-[#0a4f3a] inline-flex items-center gap-1"><CheckCheck className="w-4 h-4" /> {lang === "ar" ? "حُفظ" : "Saved"}</span>}
        <button onClick={save} disabled={saving} className="btn-primary" data-testid="notif-pref-save">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
          {lang === "ar" ? "حفظ" : "Save"}
        </button>
      </div>
    </div>
  );
}

function Switch({ checked, onChange, testid }) {
  return (
    <button type="button" onClick={onChange} className={`relative w-11 h-6 rounded-full transition-colors ${checked ? "bg-[#0a4f3a]" : "bg-[#0a0a0a]/[0.15]"}`} data-testid={testid}>
      <span className={`absolute top-0.5 ${checked ? "end-0.5" : "start-0.5"} w-5 h-5 bg-white rounded-full transition-all shadow`}></span>
    </button>
  );
}
